import React, { useState } from "react";
import { X, Search } from "lucide-react";

const ReferenceFoodMenuPanel = ({ isOpen, onClose, menuData }) => {
  const [search, setSearch] = useState("");

  // Transform menu data into sections
  const menuSections = React.useMemo(() => {
    if (!Array.isArray(menuData) || menuData.length === 0) {
      return {};
    }

    return menuData.reduce((acc, dish) => {
      if (!dish?.type?.[0]) {
        return acc;
      }

      const type = dish?.type[0];
      if (!acc[type]) {
        acc[type] = [];
      }

      // Prepare all fields with defaults if missing
      const allergens = Array.isArray(dish.allergens)
        ? dish.allergens.join(", ")
        : dish.allergens || "None";
      // Health restrictions: flatten if object, else use as string/array
      let healthRestrictions = "None";
      if (dish.dietary_restrictions) {
        if (
          typeof dish.dietary_restrictions === "object" &&
          dish.dietary_restrictions.health
        ) {
          healthRestrictions = Array.isArray(dish.dietary_restrictions.health)
            ? dish.dietary_restrictions.health.join(", ")
            : dish.dietary_restrictions.health;
        } else if (Array.isArray(dish.dietary_restrictions)) {
          healthRestrictions = dish.dietary_restrictions.join(", ");
        } else if (typeof dish.dietary_restrictions === "string") {
          healthRestrictions = dish.dietary_restrictions;
        }
      }
      // Belief accommodations
      let beliefAccommodations = "None";
      if (
        dish.dietary_restrictions &&
        typeof dish.dietary_restrictions === "object" &&
        dish.dietary_restrictions.belief
      ) {
        beliefAccommodations = Array.isArray(dish.dietary_restrictions.belief)
          ? dish.dietary_restrictions.belief.join(", ")
          : dish.dietary_restrictions.belief;
      }
      // Lifestyle accommodations
      let lifestyleAccommodations = "None";
      if (
        dish.dietary_restrictions &&
        typeof dish.dietary_restrictions === "object" &&
        dish.dietary_restrictions.lifestyle
      ) {
        lifestyleAccommodations = Array.isArray(
          dish.dietary_restrictions.lifestyle
        )
          ? dish.dietary_restrictions.lifestyle.join(", ")
          : dish.dietary_restrictions.lifestyle;
      }
      // Substitution accommodations
      let substitutionAccommodations = "None";
      const subsField = dish.substitutions || dish.substitution_accommodations;
      if (subsField) {
        substitutionAccommodations = Array.isArray(subsField)
          ? subsField
          : [subsField];
      }
      let can_substitute = dish.can_substitute;
      let accommodations = "None";
      if (dish.accommodations) {
        accommodations = Array.isArray(dish.accommodations)
          ? dish.accommodations.join(", ")
          : dish.accommodations;
      }

      let substitutionNotes = dish.substitution_notes || "None";
      let temperature = dish?.temperature || "None";
      let ingredients = Array.isArray(dish.ingredients)
        ? dish.ingredients.join(", ")
        : dish.ingredients || "None";

      acc[type].push({
        name: dish?.name,
        price: `$${dish?.price?.toFixed(2)}`,
        description: dish?.description,
        cross_contact: dish?.is_cross_contact,
        accommodations,
        allergens,
        healthRestrictions,
        beliefAccommodations,
        lifestyleAccommodations,
        substitutionAccommodations,
        substitutionNotes,
        can_substitute,
        temperature,
        ingredients,
        notes: dish?.notes,
        image_url: dish?.image_url || null,
      });
      return acc;
    }, {});
  }, [menuData]);

  // Convert sections object to array format
  const formattedMenu = React.useMemo(
    () =>
      Object.entries(menuSections || {}).map(([section, items]) => ({
        section,
        items,
      })),
    [menuSections]
  );

  // Filter menu by search
  const filteredMenu = React.useMemo(
    () =>
      formattedMenu
        .map((section) => ({
          ...section,
          items: section?.items?.filter((item) => {
            const searchLower = search?.toLowerCase();
            return (
              item?.name?.toLowerCase()?.includes(searchLower) ||
              item?.description?.toLowerCase()?.includes(searchLower) ||
              item?.badges?.some((b) =>
                b?.label?.toLowerCase()?.includes(searchLower)
              )
            );
          }),
        }))
        .filter((section) => section.items.length > 0),
    [formattedMenu, search]
  );

  if (!isOpen) return null;
  return (
    <div className="fixed  inset-y-0 right-0 w-full sm:max-w-sm md:max-w-sm lg:max-w-sm bg-[#FFF9F3] shadow-lg z-[200] transition-transform duration-300 ease-in-out flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-3 sm:px-4 py-3 border-b border-gray-200">
        <h2 className="text-base text-left font-semibold text-black">
          Reference Menu
        </h2>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-red-500 p-1 rounded-full"
        >
          <X size={20} />
        </button>
      </div>
      {/* Search Bar */}
      <div className="px-3 sm:px-4 pb-2 pt-3">
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#C1121F]"
          />
          <input
            type="text"
            placeholder="Search dishes or ingredients..."
            className="w-full pl-9 pr-3 py-2 rounded-md bg-background text-black text-sm placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-[#FFD6A0]"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>
      {/* Menu Sections */}
      <div className="flex-1 overflow-y-auto px-3 sm:px-4 pt-2 pb-4 space-y-6">
        {filteredMenu.length === 0 && (
          <div className="text-center text-gray-500 text-sm mt-8">
            {search
              ? `No results found for "${search}"`
              : "No menu items available"}
          </div>
        )}
        {filteredMenu.map((section) => (
          <div key={section.section}>
            <h3 className="text-[10px] sm:text-[12px] font-bold text-orange-600 px-2 py-0.5 bg-[#FFF3D0] rounded w-fit mb-3">
              {section.section}
            </h3>
            <div className="space-y-4">
              {section.items.map((item) => (
                <div
                  key={item.name}
                  className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow group flex flex-col h-full"
                >
                  {/* Card Header with Image and Badges */}
                  <div className="relative h-48 w-full flex-shrink-0">
                    {item.image_url ? (
                      <img
                        src={
                          item.image_url.startsWith("http")
                            ? item.image_url
                            : `${process.env.REACT_APP_IMAGE_BASE_URL}${item.image_url.startsWith("/") ? "" : "/"
                            }${item.image_url}`
                        }
                        alt={item.name}
                        referrerPolicy="no-referrer"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full bg-gray-50 flex items-center justify-center text-gray-300">
                        <svg
                          className="w-12 h-12"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </div>
                    )}

                    {item.temperature !== "None" && (
                      <div className="absolute top-3 left-3 flex gap-2">
                        <span className="px-2 py-0.5 bg-white shadow-sm rounded-full text-[12px] font-bold text-gray-600">
                          {item.temperature}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Card Body */}
                  <div className="p-4 flex text-start flex-col flex-grow space-y-4 justify-between min-w-0">
                    <div className="space-y-4 min-w-0">

                      {/* Flex Header for Name and Price */}
                      <div className="flex items-start justify-between gap-2 min-w-0">
                        <div className="space-y-1 min-w-0 flex-1">
                          <h3 className="text-sm sm:text-base font-black text-gray-900 leading-tight break-words">
                            {item.name}
                          </h3>
                        </div>
                        <span className="text-[12px] font-black text-purple-600 px-2 py-0.5 bg-purple-50 rounded-lg flex-shrink-0 whitespace-nowrap">
                          {item.price}
                        </span>
                      </div>

                      {/* Description */}
                      {item.description && (
                        <p className="text-[11px] sm:text-[12px] text-gray-600 font-medium leading-snug italic break-words">
                          {item.description.length > 50
                            ? item.description.substring(0, 50) + "..."
                            : item.description}
                        </p>
                      )}

                      {/* Stacked Details Using Original Order */}
                      <div className="space-y-4 min-w-0 pt-2 border-t border-gray-50">
                        {/* Allergens */}
                        <div className="min-w-0">
                          <span className="text-[10px] sm:text-[12px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5">
                            Allergens
                          </span>
                          <div className="flex flex-wrap gap-1 min-w-0">
                            {item.allergens && item.allergens !== "None" ? (
                              item.allergens.split(', ').map((alg, i) => (
                                <span key={i} className="px-2 py-0.5 bg-red-50 text-red-600 rounded text-[10px] sm:text-[12px] font-bold border border-red-100 capitalize break-words">
                                  {alg}
                                </span>
                              ))
                            ) : (
                              <span className="text-[10px] sm:text-[12px] text-gray-400 italic">None</span>
                            )}
                          </div>
                        </div>

                        {/* Health Restrictions */}
                        <div className="min-w-0">
                          <span className="text-[10px] sm:text-[12px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5">
                            Health Restrictions
                          </span>
                          <div className="flex flex-wrap gap-1 min-w-0">
                            {item.healthRestrictions && item.healthRestrictions !== "None" ? (
                              item.healthRestrictions.split(', ').map((h, i) => (
                                <span key={i} className="px-2 py-0.5 border bg-blue-50 text-blue-600 border-blue-100 rounded text-[10px] sm:text-[12px] font-bold capitalize break-words">
                                  {h}
                                </span>
                              ))
                            ) : (
                              <span className="text-[10px] sm:text-[12px] text-gray-400 italic">None</span>
                            )}
                          </div>
                        </div>

                        {/* Belief Accommodations */}
                        <div className="min-w-0">
                          <span className="text-[10px] sm:text-[12px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5">
                            Belief Accommodations
                          </span>
                          <div className="flex flex-wrap gap-1 min-w-0">
                            {item.beliefAccommodations && item.beliefAccommodations !== "None" ? (
                              item.beliefAccommodations.split(', ').map((b, i) => (
                                <span key={i} className="px-2 py-0.5 border bg-purple-50 text-purple-600 border-purple-100 rounded text-[10px] sm:text-[12px] font-bold capitalize break-words">
                                  {b}
                                </span>
                              ))
                            ) : (
                              <span className="text-[10px] sm:text-[12px] text-gray-400 italic">None</span>
                            )}
                          </div>
                        </div>

                        {/* Lifestyle Accommodations */}
                        <div className="min-w-0">
                          <span className="text-[10px] sm:text-[12px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5">
                            Lifestyle Accommodations
                          </span>
                          <div className="flex flex-wrap gap-1 min-w-0">
                            {item.lifestyleAccommodations && item.lifestyleAccommodations !== "None" ? (
                              item.lifestyleAccommodations.split(', ').map((l, i) => (
                                <span key={i} className="px-2 py-0.5 border bg-green-50 text-green-600 border-green-100 rounded text-[10px] sm:text-[12px] font-bold capitalize break-words">
                                  {l}
                                </span>
                              ))
                            ) : (
                              <span className="text-[10px] sm:text-[12px] text-gray-400 italic">None</span>
                            )}
                          </div>
                        </div>

                        {/* Accommodations (Inherent) */}
                        <div className="min-w-0">
                          <span className="text-[10px] sm:text-[12px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5">
                            Accommodations (Inherent)
                          </span>
                          <div className="flex flex-wrap gap-1 min-w-0">
                            {item.accommodations && item.accommodations !== "None" ? (
                              item.accommodations.split(', ').map((acc, i) => (
                                <span key={i} className="px-2 py-0.5 bg-yellow-50 text-yellow-700 border border-yellow-100 rounded text-[10px] sm:text-[12px] font-bold capitalize break-words">
                                  {acc}
                                </span>
                              ))
                            ) : (
                              <span className="text-[10px] sm:text-[12px] text-gray-400 italic">None</span>
                            )}
                          </div>
                        </div>

                        {/* Bottom Detail Sections (Ingredients, Substitutions, Cross Contact, Notes) exactly as MenuItemsTable */}
                        <div className="space-y-2 border-t border-gray-50 pt-3 min-w-0">
                          {/* Ingredients */}
                          <div className="flex items-center gap-2 flex-wrap mb-1.5">
                            <div className="flex items-center gap-1.5 px-3 py-1 bg-gray-50 text-gray-700 rounded-lg text-[10px] sm:text-[12px] font-bold whitespace-nowrap">
                              <svg
                                className="w-3.5 h-3.5 flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                              Ingredients
                            </div>
                          </div>
                          <p className="text-[11px] sm:text-[12px] text-gray-600 font-medium leading-relaxed italic break-words pb-2">
                            {item.ingredients === "None" ? "No ingredients listed." : item.ingredients}
                          </p>

                          {/* Substitution Accommodations - Arrows Style */}
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between py-1 gap-2 mt-1">
                            <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-50/50 text-orange-700 rounded-lg text-[10px] sm:text-[12px] font-bold w-fit">
                              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                              </svg>
                              Substitutions
                            </div>
                            <span className="text-[10px] sm:text-[12px] font-bold text-gray-400 whitespace-nowrap">
                              Available:{" "}
                              <span
                                className={
                                  (item.can_substitute === true || item.can_substitute === "Yes")
                                    ? "text-green-600"
                                    : "text-gray-500"
                                }
                              >
                                {(item.can_substitute === true || item.can_substitute === "Yes") ? "Yes" : "No"}
                              </span>
                            </span>
                          </div>
                          <div className="mt-1 ml-2 space-y-1 min-w-0">
                            {Array.isArray(item.substitutionAccommodations) && item.substitutionAccommodations.length > 0 && (
                              <div className="flex flex-wrap gap-1 min-w-0 pb-1">
                                {item.substitutionAccommodations.map((sub, i) => (
                                  <span
                                    key={i}
                                    className="px-2 py-0.5 bg-gray-50 text-gray-600 border border-gray-100 rounded text-[10px] sm:text-[11px] font-medium break-words"
                                  >
                                    {sub}
                                  </span>
                                ))}
                              </div>
                            )}
                            {item.substitutionNotes && item.substitutionNotes !== "None" && (
                              <p className="text-[10px] sm:text-[11px] text-gray-500 italic break-words pb-1">
                                Note: {item.substitutionNotes}
                              </p>
                            )}
                          </div>

                          {/* Cross Contact - Warning Style */}
                          <div className="flex items-center gap-1.5 px-2 py-1 bg-red-50/50 text-red-700 rounded-lg text-[10px] sm:text-[12px] font-bold w-fit mt-1">
                            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            Cross Contact
                          </div>
                          <span className="text-[10px] sm:text-[12px] font-bold text-gray-600 ml-2 mt-0.5 pb-1 block break-words">
                            Cross Contact:{" "}
                            <span className={item.cross_contact ? "text-red-600" : "text-gray-500"}>
                              {item.cross_contact ? "Yes" : "No"}
                            </span>
                          </span>

                          {/* Additional Notes - Chat Bubble Style */}
                          <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50/50 text-blue-700 rounded-lg text-[10px] sm:text-[12px] font-bold w-fit mt-1">
                            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                            </svg>
                            Notes
                          </div>
                          <p className="text-[10px] sm:text-[12px] text-gray-400 italic ml-2 mt-0.5 break-words">
                            {item.notes || "None"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReferenceFoodMenuPanel;
